/**
 * Reward Claim Widget Component
 * 
 * Dual-mode widget for claiming rewards:
 * - Desktop: Compact inline element for header integration
 * - Mobile: Floating widget in bottom-right corner
 */

import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Gift, X, Loader2, ChevronRight, Sparkles, Download, DollarSign } from 'lucide-react'
import { Button } from '../ui/button'
import { apiClient } from '../../lib/api/client'
import { toast } from '../../hooks/use-toast'
import { notifyWalletUpdate } from '../../lib/events/wallet-events'
import { SuccessDialog } from '../ui/SuccessDialog'
import { useAuth } from '../../contexts/AuthContext'
import { useIsMobile } from '../../hooks/useIsMobile'
import { cn } from '../../lib/utils'
import type { UserReward } from '../../types'

// Helper to check if a reward is referral-related (uses purple color)
const isReferralReward = (reward: UserReward): boolean => {
  return reward.source_referral_id !== null || reward.reward?.reward_type === 'bonus_downloads'
}

// Helper to get display value for a reward
const getRewardDisplayValue = (reward: UserReward, takesLabel: string): string => {
  if (reward.reward?.reward_type === 'bonus_downloads') {
    return `+${reward.reward.bonus_download_amount} ${takesLabel}`
  }
  return `$${reward.reward?.credit_amount_usd.toFixed(2)}`
}

// Helper to get the appropriate icon for a reward
const getRewardIcon = (reward: UserReward) => {
  if (reward.reward?.reward_type === 'bonus_downloads') {
    return <Download className="h-4 w-4" />
  }
  return <DollarSign className="h-4 w-4" />
}

interface RewardClaimWidgetProps {
  /** Widget display mode */
  mode: 'desktop-header' | 'mobile-floating'
  /** Callback when balance updates */
  onBalanceUpdate?: (newBalance: number) => void
}

interface ClaimResult {
  rewardName: string
  creditAmount: number
  newBalance: number
  rewardType: 'credits' | 'bonus_downloads'
  bonusDownloadsAmount?: number
}

export function RewardClaimWidget({ mode, onBalanceUpdate }: RewardClaimWidgetProps) {
  const { t } = useTranslation('common')
  const { user } = useAuth()
  const isMobile = useIsMobile()
  const [rewards, setRewards] = useState<UserReward[]>([])
  const [loading, setLoading] = useState(true)
  const [claiming, setClaiming] = useState<number | null>(null)
  const [dismissed, setDismissed] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [showSuccessDialog, setShowSuccessDialog] = useState(false)
  const [claimResult, setClaimResult] = useState<ClaimResult | null>(null)

  useEffect(() => {
    if (user) {
      loadPendingRewards()
    }
  }, [user])

  const loadPendingRewards = async () => {
    try {
      setLoading(true)
      const response = await apiClient.rewards.getPendingRewards()
      setRewards(response.rewards)
    } catch (error) {
      console.error('Error loading pending rewards:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleClaim = async (userRewardId: number) => {
    try {
      setClaiming(userRewardId)
      
      const rewardToClaim = rewards.find(r => r.id === userRewardId)
      const result = await apiClient.rewards.claimReward(userRewardId)
      
      if (result.success) {
        const rewardType = rewardToClaim?.reward?.reward_type === 'bonus_downloads' ? 'bonus_downloads' : 'credits'
        setClaimResult({
          rewardName: rewardToClaim?.reward?.name || 'Reward',
          creditAmount: result.credit_amount || 0,
          newBalance: result.new_balance || 0,
          rewardType,
          bonusDownloadsAmount: rewardToClaim?.reward?.bonus_download_amount || 0,
        })
        setShowSuccessDialog(true)
        
        if (result.new_balance !== undefined) {
          notifyWalletUpdate(result.new_balance)
        }
        
        if (result.new_balance !== undefined && onBalanceUpdate) {
          onBalanceUpdate(result.new_balance)
        }
        
        setRewards(prev => prev.filter(r => r.id !== userRewardId))
      } else {
        toast({
          title: t('rewards.claimFailed'),
          description: result.message,
          variant: 'destructive',
        })
      }
    } catch (error: any) {
      console.error('Error claiming reward:', error)
      toast({
        title: t('rewards.claimFailed'),
        description: error.message || t('errors.generic'),
        variant: 'destructive',
      })
    } finally {
      setClaiming(null)
    }
  }

  const handleClaimAll = async () => {
    for (const reward of rewards) {
      await handleClaim(reward.id)
    }
  }

  const handleSuccessDialogClose = () => {
    setShowSuccessDialog(false)
    setClaimResult(null)
  }

  const shouldShow = !loading && !dismissed && rewards.length > 0
  
  // Translated label for takes (used in getRewardDisplayValue)
  const takesLabel = t('referrals.takes', 'takes')

  // Render success dialog (always render if showing)
  if (showSuccessDialog && claimResult) {
    const isDownloadReward = claimResult.rewardType === 'bonus_downloads'
    return (
      <SuccessDialog
        open={showSuccessDialog}
        onClose={handleSuccessDialogClose}
        variant="reward"
        title={t('rewards.rewardClaimed', '{{name}} Claimed!', { name: claimResult.rewardName })}
        description={isDownloadReward 
          ? t('rewards.takesAddedToAccount', 'Bonus takes have been added to your account.')
          : t('rewards.creditAddedToWallet', 'Credits have been added to your wallet.')
        }
        details={
          <div className="text-center space-y-2">
            {isDownloadReward ? (
              <p className="text-2xl font-bold text-purple-600">
                +{claimResult.bonusDownloadsAmount} {t('referrals.takes', 'takes')}
              </p>
            ) : (
              <>
                <p className="text-2xl font-bold text-green-600">
                  +${claimResult.creditAmount.toFixed(2)}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t('payment.newBalance', 'New balance')}: ${claimResult.newBalance.toFixed(2)}
                </p>
              </>
            )}
          </div>
        }
      />
    )
  }

  if (!shouldShow) return null

  // ============================================
  // DESKTOP HEADER MODE
  // ============================================
  if (mode === 'desktop-header') {
    // Determine if any rewards are referral-based for styling
    const hasReferralRewards = rewards.some(isReferralReward)
    const hasOnlyCreditRewards = rewards.every(r => r.reward?.reward_type === 'credits' && r.source_referral_id === null)
    
    return (
      <div className="reward-header-widget relative">
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "reward-trigger relative h-9 px-3 border",
            hasReferralRewards && !hasOnlyCreditRewards
              ? "bg-gradient-to-r from-purple-500/10 to-pink-500/10 hover:from-purple-500/20 hover:to-pink-500/20 border-purple-500/30 text-purple-600 dark:text-purple-400"
              : "bg-gradient-to-r from-emerald-500/10 to-teal-500/10 hover:from-emerald-500/20 hover:to-teal-500/20 border-emerald-500/30 text-emerald-600 dark:text-emerald-400"
          )}
          onClick={() => setExpanded(!expanded)}
        >
          <Gift className="h-4 w-4 mr-2 animate-bounce" />
          <span className="font-medium">
            {t('rewards.claim', 'Claim')}
          </span>
          {rewards.length > 1 && (
            <span className="ml-1.5 px-1.5 py-0.5 text-xs rounded-full bg-white/20">
              {rewards.length}
            </span>
          )}
          {/* Glow effect */}
          <div className={cn(
            "absolute inset-0 rounded-md animate-pulse pointer-events-none",
            hasReferralRewards && !hasOnlyCreditRewards
              ? "bg-gradient-to-r from-purple-500/20 to-pink-500/20"
              : "bg-gradient-to-r from-emerald-500/20 to-teal-500/20"
          )} />
        </Button>

        {/* Dropdown panel */}
        {expanded && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setExpanded(false)} 
            />
            
            {/* Panel */}
            <div className="reward-dropdown absolute right-0 top-full mt-2 z-50 w-72 rounded-lg bg-white dark:bg-gray-800 shadow-xl border border-emerald-500/30 overflow-hidden">
              {/* Header */}
              <div className="reward-dropdown-header px-4 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5" />
                  <span className="font-semibold">
                    {t('rewards.pendingRewards', 'Pending Rewards')}
                  </span>
                </div>
              </div>

              {/* Rewards list */}
              <div className="reward-dropdown-content p-3 space-y-2 max-h-64 overflow-y-auto">
                {rewards.map(reward => {
                  const isReferral = isReferralReward(reward)
                  return (
                    <div 
                      key={reward.id}
                      className={cn(
                        "reward-item flex items-center justify-between p-3 rounded-md border",
                        isReferral
                          ? "bg-gradient-to-r from-purple-500/5 to-pink-500/5 border-purple-500/20"
                          : "bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700"
                      )}
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0 mr-3">
                        <div className={cn(
                          "icon-wrapper p-1.5 rounded",
                          isReferral ? "bg-purple-500/10 text-purple-600" : "bg-emerald-500/10 text-emerald-600"
                        )}>
                          {getRewardIcon(reward)}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">
                            {reward.reward?.name}
                          </p>
                          <p className={cn(
                            "text-lg font-bold",
                            isReferral ? "text-purple-600" : "text-emerald-600"
                          )}>
                            {getRewardDisplayValue(reward, takesLabel)}
                          </p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        className={cn(
                          "reward-claim-btn text-white",
                          isReferral
                            ? "bg-purple-500 hover:bg-purple-600"
                            : "bg-emerald-500 hover:bg-emerald-600"
                        )}
                        onClick={() => handleClaim(reward.id)}
                        disabled={claiming !== null}
                      >
                        {claiming === reward.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          t('rewards.claim')
                        )}
                      </Button>
                    </div>
                  )
                })}
              </div>

              {/* Footer */}
              {rewards.length > 1 && (
                <div className="reward-dropdown-footer p-3 border-t border-gray-200 dark:border-gray-700">
                  <Button
                    className={cn(
                      "claim-all-btn w-full text-white",
                      hasReferralRewards && !hasOnlyCreditRewards
                        ? "bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                        : "bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                    )}
                    onClick={handleClaimAll}
                    disabled={claiming !== null}
                  >
                    {claiming !== null ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : null}
                    {t('rewards.claimAll', 'Claim all')}
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    )
  }

  // ============================================
  // MOBILE FLOATING MODE
  // ============================================
  // Don't render on desktop for mobile-floating mode
  if (!isMobile) return null
  
  // Determine if any rewards are referral-based for styling
  const hasReferralRewards = rewards.some(isReferralReward)
  const hasOnlyCreditRewards = rewards.every(r => r.reward?.reward_type === 'credits' && r.source_referral_id === null)
  
  return (
    <div className="reward-floating-widget">
      {!expanded ? (
        // Collapsed: Floating action button
        <button
          className={cn(
            "reward-fab relative flex items-center justify-center w-14 h-14 rounded-full text-white shadow-lg hover:shadow-xl transition-all hover:scale-105 active:scale-95",
            hasReferralRewards && !hasOnlyCreditRewards
              ? "bg-gradient-to-r from-purple-500 to-pink-500"
              : "bg-gradient-to-r from-emerald-500 to-teal-500"
          )}
          onClick={() => setExpanded(true)}
        >
          <Gift className="h-6 w-6 animate-bounce" />
          {/* Badge */}
          <span className={cn(
            "absolute -top-1 -right-1 flex items-center justify-center min-w-[22px] h-[22px] px-1 rounded-full bg-white text-xs font-bold shadow",
            hasReferralRewards && !hasOnlyCreditRewards ? "text-purple-600" : "text-emerald-600"
          )}>
            {rewards.length}
          </span>
          {/* Pulse ring */}
          <span className={cn(
            "absolute inset-0 rounded-full animate-ping opacity-30",
            hasReferralRewards && !hasOnlyCreditRewards ? "bg-purple-500" : "bg-emerald-500"
          )} />
        </button>
      ) : (
        // Expanded: Full widget
        <>
          {/* Backdrop for outside click */}
          <div 
            className="fixed inset-0 z-30" 
            onClick={() => setExpanded(false)} 
          />
          <div className={cn(
            "reward-panel relative z-40 w-72 rounded-lg bg-white dark:bg-gray-800 shadow-xl border overflow-hidden",
            hasReferralRewards && !hasOnlyCreditRewards ? "border-purple-500/30" : "border-emerald-500/30"
          )}>
          {/* Header */}
          <div className={cn(
            "reward-panel-header px-4 py-3 text-white flex items-center justify-between",
            hasReferralRewards && !hasOnlyCreditRewards
              ? "bg-gradient-to-r from-purple-500 to-pink-500"
              : "bg-gradient-to-r from-emerald-500 to-teal-500"
          )}>
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              <span className="font-semibold">
                {t('rewards.pendingRewards', 'Pending Rewards')}
              </span>
            </div>
            <button
              onClick={() => setExpanded(false)}
              className="close-btn p-1 hover:bg-white/20 rounded transition-colors"
              aria-label={t('common.close')}
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Rewards list */}
          <div className="reward-panel-content p-3 space-y-2 max-h-48 overflow-y-auto">
            {rewards.map(reward => {
              const isReferral = isReferralReward(reward)
              return (
                <div 
                  key={reward.id}
                  className={cn(
                    "reward-item flex items-center justify-between p-3 rounded-md border",
                    isReferral
                      ? "bg-gradient-to-r from-purple-500/5 to-pink-500/5 border-purple-500/20"
                      : "bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700"
                  )}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0 mr-3">
                    <div className={cn(
                      "icon-wrapper p-1.5 rounded",
                      isReferral ? "bg-purple-500/10 text-purple-600" : "bg-emerald-500/10 text-emerald-600"
                    )}>
                      {getRewardIcon(reward)}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">
                        {reward.reward?.name}
                      </p>
                      <p className={cn(
                        "text-lg font-bold",
                        isReferral ? "text-purple-600" : "text-emerald-600"
                      )}>
                        {getRewardDisplayValue(reward, takesLabel)}
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    className={cn(
                      "reward-claim-btn text-white",
                      isReferral
                        ? "bg-purple-500 hover:bg-purple-600"
                        : "bg-emerald-500 hover:bg-emerald-600"
                    )}
                    onClick={() => handleClaim(reward.id)}
                    disabled={claiming !== null}
                  >
                    {claiming === reward.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      t('rewards.claim')
                    )}
                  </Button>
                </div>
              )
            })}
          </div>

          {/* Footer */}
          {rewards.length > 1 && (
            <div className="reward-panel-footer p-3 border-t border-gray-200 dark:border-gray-700">
              <Button
                className={cn(
                  "claim-all-btn w-full text-white",
                  hasReferralRewards && !hasOnlyCreditRewards
                    ? "bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                    : "bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                )}
                onClick={handleClaimAll}
                disabled={claiming !== null}
              >
                {claiming !== null ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                {t('rewards.claimAll', 'Claim all')}
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}

          {/* Dismiss link */}
          <div className="px-3 pb-3">
            <button
              onClick={() => setDismissed(true)}
              className="dismiss-link w-full text-center text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              {t('common.dismissForNow', 'Dismiss for now')}
            </button>
          </div>
        </div>
        </>
      )}
    </div>
  )
}

